import { useCallback } from 'react';
import { useTour } from 'context/TourContext';

/**
 * Custom hook for managing focus mode in components
 * @returns {Object} Object containing focus mode functions and state
 */
export const useFocusMode = () => {
  const { 
    focusMode, 
    setFocusMode, 
    focusModeOptions, 
    setFocusModeOptions 
  } = useTour();

  /**
   * Enable focus mode with custom options
   * @param {Object} options - Focus mode options
   * @param {string} options.overlayColor - Color of the overlay (default: 'rgba(0, 0, 0, 0.5)')
   * @param {boolean} options.allowClickThrough - Whether to allow clicking through the overlay (default: false)
   * @param {boolean} options.highlightBeacon - Whether to show highlight around beacon (default: true)
   * @param {boolean} options.disableScroll - Whether to disable scrolling (default: false)
   */
  const enableFocusMode = useCallback((options = {}) => {
    setFocusMode(true);
    if (Object.keys(options).length > 0) {
      setFocusModeOptions(prev => ({
        ...prev,
        ...options
      }));
    }
  }, [setFocusMode, setFocusModeOptions]);

  /**
   * Disable focus mode
   */
  const disableFocusMode = useCallback(() => {
    setFocusMode(false);
  }, [setFocusMode]);

  /**
   * Toggle focus mode
   */
  const toggleFocusMode = useCallback(() => {
    setFocusMode(prev => !prev);
  }, [setFocusMode]);

  /**
   * Update focus mode options
   * @param {Object} options - New focus mode options
   */
  const updateFocusModeOptions = useCallback((options) => {
    setFocusModeOptions(prev => ({
      ...prev,
      ...options
    }));
  }, [setFocusModeOptions]);

  return {
    focusMode,
    enableFocusMode,
    disableFocusMode,
    toggleFocusMode,
    focusModeOptions,
    updateFocusModeOptions
  };
};

export default useFocusMode;